import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { toast } from "react-toastify";
import styles from "./productOwnership.module.css";

/**
 * WHO OWNS THIS PRODUCT
 *
 * A product is either VENDOR-OWNED (one vendor listed it and names everything
 * on it) or HOUSE-OWNED — a shared shelf where Easexpense governs the names
 * and any vendor may quote. Until phase 3 there was no way to see which, and
 * no way to convert one deliberately: it only happened automatically, the
 * moment a second vendor's offers were approved.
 *
 * Taking ownership changes exactly one thing: who may author plan NAMES. It
 * never moves a seller, a price, a payout or a subscription — the vendor who
 * listed the product keeps selling everything they were selling. The dialog
 * says so, because "take ownership" reads like a much bigger button than it is.
 */

const API = process.env.REACT_APP_SERVER_URL;

function ProductOwnership({ product, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!product?.id) return null;

  const houseOwned =
    Number(product.vendor_id) === -1 || product.owner_name === "Admin";
  const ownerLabel = houseOwned
    ? "Shared listing — Easexpense governs the plan names"
    : `Vendor-owned — ${product.owner_name || `vendor #${product.vendor_id}`} names the plans`;

  const takeOwnership = async () => {
    setBusy(true);
    try {
      const res = await fetch(
        `${API}/admin/products/${product.id}/take-ownership`,
        {
          method: "POST",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({}),
        },
      );
      const body = await res.json();
      if (!body.success) {
        toast.error(body.msg || "Failed to take ownership");
        setBusy(false);
        return;
      }
      toast.success(body.msg);
      setOpen(false);
      if (typeof onChanged === "function") {
        onChanged({
          ...product,
          vendor_id: body.house_vendor_id,
          owner_name: "Admin",
        });
      }
    } catch {
      toast.error("Failed to take ownership");
    }
    setBusy(false);
  };

  return (
    <div className={`${styles.Bar} ${houseOwned ? styles.House : styles.Vendor}`}>
      <span className={styles.Dot} aria-hidden="true" />
      <div className={styles.Text}>
        <strong className={styles.Label}>{ownerLabel}</strong>
        <span className={styles.Note}>
          {houseOwned
            ? "Plan names are frozen and change only here. Every vendor keeps selling what they were selling."
            : "Only this vendor can rename plans on this product. Other vendors may still quote and sell."}
        </span>
      </div>
      {!houseOwned ? (
        <Button
          size="small"
          variant="outlined"
          onClick={() => setOpen(true)}
          className={styles.Action}
        >
          Take ownership
        </Button>
      ) : null}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Make this a shared listing?</DialogTitle>
        <DialogContent>
          <p className={styles.DialogLead}>
            Easexpense takes over the plan NAMES on{" "}
            <strong>{product.name}</strong>. That is the whole change.
          </p>
          <ul className={styles.DialogList}>
            <li>
              Plan names freeze at what they are now, and from then on change
              only by admin — including for{" "}
              {product.owner_name || "the current owner"}, who can ask for a
              rename but not make one.
            </li>
            <li>
              {product.owner_name || "The current owner"} keeps selling every
              shelf they were selling, at the same prices and payouts, with
              every live subscription untouched.
            </li>
            <li>
              Any vendor may quote on this product's shelves from then on. Who
              sells each shelf is still your decision and nothing moves today.
            </li>
            <li>Buyers see no difference.</li>
          </ul>
          <p className={styles.DialogNote}>
            The current owner is emailed. This cannot be undone from here — a
            shared product does not go back to one vendor.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={takeOwnership} disabled={busy}>
            {busy ? "Working…" : "Take ownership"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ProductOwnership;
